import {
  ActionButton,
  Flex,
  Text,
  TooltipTrigger,
  Tooltip,
  View
} from '@adobe/react-spectrum';
import React, { useMemo, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  selectSearchFilterActive,
  selectSearchFilteredItems,
  selectProductGroups,
  selectAudiences,
  selectCategories,
  selectFeatures,
  selectGenAiFilter,
  selectProductFilter,
  selectHighlightedIssue,
  selectEvents,
  selectRevenueImpactFilter,
  selectStatus,
  selectMomentTypeFilter,
  selectRsdMomentTypes
} from 'app/pages/Home/slice/selectors';
import {
  excludeEolFromMomentTypeSelection,
  subProjectMatchesMomentTypeSelection
} from 'app/components/Header/momentTypeRoadmapFilter';
import styles from './ParkingLot.module.scss';
import { useParkingLotWheelScrollContain } from './useParkingLotWheelScrollContain';
import {
  openDocument,
  isBetaLabel,
  isPrivateBetaLabel,
  isBetaOrPrivateBetaLabel,
  getReleasePhaseIcon
} from 'utils/helpers';
import { useSlideoutUrl } from 'app/hooks/useSlideoutUrl';
import { RevenueDriverIcon } from 'app/assets/legend/RevenueDriverIcon';
import { isEolMoment, RISK_LEVELS } from 'utils/constants';
import {
  formatTimelineDateChipLabel,
  getParkingLotDisplayDates,
  type MomentDates
} from 'utils/timelineDisplayDates';
import { getRtmPhasedTitleParts } from 'utils/rtmPhaseDisplayTitle';
import { CardTitleDisplay } from '../TimelineCard/CardTitleDisplay';
import { formatEolPhaseDisplayTitle } from 'utils/eolPhaseTitlePrefix';
import { ProductIconCompact } from '../TimelineCard/ProductIconCompact';
import { LegendBetaMiniIcon } from 'app/assets/legend/LegendBetaMiniIcon';
import { LegendPrivateBetaMiniIcon } from 'app/assets/legend/LegendPrivateBetaMiniIcon';
import { selectTimelineIconsVisible } from 'app/components/MonthlyTimeline/store/selectors';

interface Project {
  title: string;
  issueId?: string;
  tier?: string;
  priority?: string;
  riskLevel?: string;
  labels: string[];
  links: Array<{ type: string; reference: string }>;
  releasePhase?: string;
  dates?: MomentDates;
  /** Often present on API payloads; also read by shared moment-type matcher (may differ from RSD names). */
  projectType?: string;
  showOnTimeline?: boolean;
  /** True for legacy PROMOS_OFFERS moments that follow Price Changes date logic. */
  pco?: boolean;
  revenueImpact?: boolean;
  releasePhases?: Array<{
    phaseName: string;
    generalInfo?: {
      revenueImpact?: boolean;
    };
    status?: string;
  }>;
  customerAudiences?: string | string[];
  productGroups?: string | string[];
  categories?: string | string[];
  products?: string | string[];
  features?: Array<{
    featureId?: string;
    issueId?: string;
    surfaces?: string[];
    genai?: boolean;
    consumesCredit?: boolean;
  }>;
}

interface ParkingLotItem {
  project: Project[];
}

interface ParkingLotProps {
  showParkingLot: boolean;
  data?: ParkingLotItem[];
  isFullScreenMode?: boolean;
  /** Milestones grid report: EOL moment-type filter is roadmap-only. */
  hideEolMomentType?: boolean;
}

// ---------- Small helpers reused from Roadmap filters ----------

const matchesAudience = (project: any, selected: string[]) => {
  if (selected.length === 0) return true;
  const audiences = project.customerAudiences;
  if (Array.isArray(audiences)) {
    return audiences.some((aud: string) => selected.includes(aud));
  }
  return selected.includes(audiences || '');
};

const matchesProductGroup = (project: any, selected: string[]) => {
  if (selected.length === 0) return true;
  const groups = project.productGroups;
  if (Array.isArray(groups)) {
    return groups.some((g: string) => selected.includes(g));
  }
  return selected.includes(groups || '');
};

const matchesCategory = (project: any, selected: string[]) => {
  if (selected.length === 0) return true;
  const categories = project.categories;
  if (Array.isArray(categories)) {
    return categories.some((c: string) => selected.includes(c));
  }
  return selected.includes(categories || '');
};

const matchesProduct = (project: any, selected: string[]) => {
  if (selected.length === 0) return true;
  const products = project.products;
  if (Array.isArray(products)) {
    return products.some((p: string) => selected.includes(p));
  }
  return selected.includes(products || '');
};

const matchesStatus = (project: any, selectedStatus: string[]) => {
  if (selectedStatus.length === 0) return true;
  const riskLevel = project.riskLevel;

  return selectedStatus.some(key => {
    if (key === 'all') return true;
    if (key === 'risk-level-low' && riskLevel === RISK_LEVELS.LOW) return true;
    if (key === 'risk-level-medium' && riskLevel === RISK_LEVELS.MODERATE)
      return true;
    if (key === 'risk-level-high' && riskLevel === RISK_LEVELS.HIGH)
      return true;
    if (key === 'in-planning' && riskLevel === RISK_LEVELS.IN_PLANNING)
      return true;
    return false;
  });
};

const matchesSurface = (project: any, selectedSurfaces: string[]) => {
  if (selectedSurfaces.length === 0) return true;
  const rootSurfaces = Array.isArray(project.surfaces)
    ? (project.surfaces as string[])
    : [];
  const rootMatch = rootSurfaces.some(s => selectedSurfaces.includes(s));

  const featuresArray = Array.isArray(project.features) ? project.features : [];
  const featureMatch = featuresArray.some(
    (feature: any) =>
      Array.isArray(feature.surfaces) &&
      feature.surfaces.some((s: string) => selectedSurfaces.includes(s))
  );

  return rootMatch || featureMatch;
};

const matchesGenAi = (project: any, selectedFilters: string[]) => {
  if (selectedFilters.length === 0) return true;

  const hasAllGenAiFilter = selectedFilters.includes('gen-ai-all-features');
  const hasConsumesCreditsFilter = selectedFilters.includes(
    'gen-ai-consumes-credits'
  );

  if (!hasAllGenAiFilter && !hasConsumesCreditsFilter) {
    return true;
  }

  const featuresArray = Array.isArray(project.features) ? project.features : [];

  const featureMatches = featuresArray.some((feature: any) => {
    const isGenAi = feature.genai === true;
    const consumesCredits = feature.consumesCredit === true;
    if (hasAllGenAiFilter && isGenAi) return true;
    if (hasConsumesCreditsFilter && isGenAi && consumesCredits) return true;
    return false;
  });

  const rootGenAi = project.genai === true;
  const rootConsumesCredits = project.consumesCredit === true;
  const rootMatches =
    (hasAllGenAiFilter && rootGenAi) ||
    (hasConsumesCreditsFilter && rootGenAi && rootConsumesCredits);

  return featureMatches || rootMatches;
};

const openDocumentLink = (project: Project) => {
  const docs = project.links?.filter(link => link.type === 'DOCUMENTATION');
  if (docs && docs.length > 0 && docs[0]?.reference) {
    openDocument(docs[0]?.reference);
  }
};

const getParkingLotDateLabel = (project: Project): string => {
  return formatTimelineDateChipLabel(
    getParkingLotDisplayDates({
      dates: project.dates,
      projectType: project.projectType ?? '',
      pco: project.pco
    })
  );
};

const getParkingLotTierLabel = (project: Project): string => {
  return project.tier || project.priority || 'TBD';
};

export const ParkingLot: React.FC<ParkingLotProps> = ({
  showParkingLot,
  data,
  isFullScreenMode,
  hideEolMomentType = false
}) => {
  const [openTooltip, setOpenTooltip] = useState<string | null>(null);
  const tooltipTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Create unique tooltip identifier to avoid conflicts with duplicate issueIds
  const getUniqueTooltipId = (project: Project, projectIndex: number) => {
    const issueId = project?.issueId || projectIndex;
    const releasePhaseId = project?.releasePhase || 'unknown';
    const uniqueId = `title-${issueId}-${releasePhaseId}`;
    return uniqueId;
  };

  // Hook for slideout management
  const { openSlideout } = useSlideoutUrl();

  // Get search filter state
  const searchFilterActive = useSelector(selectSearchFilterActive);
  const searchFilteredItems = useSelector(selectSearchFilteredItems);

  // Get all filter states
  const productGroups = useSelector(selectProductGroups);
  const audiences = useSelector(selectAudiences);
  const categories = useSelector(selectCategories);
  const selectedFeatures = useSelector(selectFeatures);
  const genAiFilter = useSelector(selectGenAiFilter);
  const selectedProducts = useSelector(selectProductFilter);
  const selectedEvents = useSelector(selectEvents);
  const revenueImpactFilter = useSelector(selectRevenueImpactFilter);
  const status = useSelector(selectStatus);
  const momentTypeFilter = useSelector(selectMomentTypeFilter);
  const rsdMomentTypes = useSelector(selectRsdMomentTypes);
  const timelineIconsVisible = useSelector(selectTimelineIconsVisible);

  const effectiveMomentTypeSelection = useMemo(
    () =>
      hideEolMomentType
        ? excludeEolFromMomentTypeSelection(
            momentTypeFilter.selectedMomentTypes,
            rsdMomentTypes
          )
        : momentTypeFilter.selectedMomentTypes,
    [hideEolMomentType, momentTypeFilter.selectedMomentTypes, rsdMomentTypes]
  );

  // Get highlighted issue state for event highlighting
  const highlightedIssue = useSelector(selectHighlightedIssue);

  // Check if a parking lot project should be highlighted (for event filtering)
  const isProjectHighlighted = (project: Project) => {
    if (!project?.issueId) return false;

    // Check if the project's issueId is in the highlighted issues
    // Similar logic to TimelineCard highlighting
    const issueId = project.issueId;
    const releasePhase = project.releasePhase;
    const releasePhaseId = (project as any).releasePhaseId; // UUID

    // Check for exact match (issueId only)
    if (highlightedIssue[issueId]) return true;

    // Check for composite key match with releasePhaseId (UUID)
    if (releasePhaseId) {
      const compositeKey = `${issueId}-${releasePhaseId}-false`;
      if (highlightedIssue[compositeKey]) return true;
    }

    // Check for composite key match with releasePhase (string name)
    if (releasePhase) {
      const compositeKey = `${issueId}-${releasePhase}-false`;
      if (highlightedIssue[compositeKey]) return true;
    }

    return false;
  };

  // Helper function to check if project has revenue impact
  const hasRevenueImpact = (project: Project): boolean => {
    // Check root level first
    if (project?.revenueImpact === true) {
      return true;
    }
    // Check nested in releasePhases for active phase
    if (project?.releasePhases && Array.isArray(project.releasePhases)) {
      const activePhase =
        project.releasePhases.find((phase: any) => phase.status === 'ACTIVE') ||
        project.releasePhases[0];
      return activePhase?.generalInfo?.revenueImpact === true;
    }
    return false;
  };

  // Handle card click - prioritize slideout opening over document opening
  const handleCardClick = (project: Project) => {
    if (project?.issueId) {
      // If the project has an issueId, open the slideout
      openSlideout(project.issueId);
    } else {
      // Fallback to opening document if no issueId
      openDocumentLink(project);
    }
  };

  const handleTooltipMouseEnterWithId = (tooltipId: string) => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    tooltipTimerRef.current = setTimeout(() => {
      setOpenTooltip(tooltipId);
    }, 1000);
  };

  const handleTooltipMouseEnter = (project: Project, projectIndex: number) => {
    const uniqueId = getUniqueTooltipId(project, projectIndex);
    handleTooltipMouseEnterWithId(uniqueId);
  };

  const handleTooltipMouseLeave = () => {
    if (tooltipTimerRef.current) {
      clearTimeout(tooltipTimerRef.current);
    }
    setOpenTooltip(null);
  };

  const { rootRef, contentRef } = useParkingLotWheelScrollContain();

  return (
    <div
      ref={rootRef}
      className={styles['parking-lot']}
      data-testid="parking-lot-container"
    >
      <Flex
        direction={'column'}
        alignItems={'center'}
        gap="size-125"
        UNSAFE_className={styles['parking-lot-header']}
        data-testid="parking-lot-header"
      >
        <Text
          UNSAFE_className={styles['parking-lot-header-text']}
          data-testid="parking-lot-title"
        >
          In The Works
        </Text>
        <Text
          UNSAFE_className={styles['parking-lot-header-subtext']}
          data-testid="parking-lot-subtitle"
        >
          TIER OR MARKETING MOMENT/RELEASE DATE TBD
        </Text>
      </Flex>

      <div
        ref={contentRef}
        className={`${styles['parking-lot-content']} ${
          isFullScreenMode ? styles['fullscreen'] : ''
        }`}
        data-testid="parking-lot-content"
      >
        <Flex
          gap="size-125"
          direction={'column'}
          alignItems="center"
          UNSAFE_className={styles['parking-lot-content-container']}
          data-testid="parking-lot-items-container"
        >
          {Array.isArray(data) &&
            data.map(
              (item, itemIndex) =>
                Array.isArray(item?.project) &&
                item.project
                  .filter(project => {
                    // ⭐ SEARCH SUPERSEDES ALL OTHER FILTERS
                    // If search is active, ONLY check search matches and ignore all other filters
                    if (searchFilterActive && searchFilteredItems.length > 0) {
                      // Check plain ID match first
                      if (searchFilteredItems.includes(project.issueId || '')) {
                        return true;
                      }
                      // Check composite key match for phase-specific filtering
                      const projectAny = project as any;
                      if (projectAny.releasePhaseId !== undefined) {
                        // Try phase-only key first (matches both timeline and roadmap cards)
                        const phaseOnlyKey = `${project.issueId}-${projectAny.releasePhaseId}`;
                        if (searchFilteredItems.includes(phaseOnlyKey)) {
                          return true;
                        }

                        // Also try with showOnTimeline for backward compatibility
                        if (projectAny.showOnTimeline !== undefined) {
                          const fullCompositeKey = `${project.issueId}-${projectAny.releasePhaseId}-${projectAny.showOnTimeline}`;
                          if (searchFilteredItems.includes(fullCompositeKey)) {
                            return true;
                          }
                        }
                      }
                      return false;
                    }

                    // ⭐ EVENT FILTER WORKS WITH AND LOGIC ALONGSIDE OTHER FILTERS
                    // If events are selected, items must be highlighted AND match other filters
                    const eventMatch =
                      selectedEvents.selectedEvents.length === 0 ||
                      isProjectHighlighted(project);

                    // ⭐ APPLY ALL FILTERS WITH AND LOGIC
                    // Apply audience/product group/category/product filters using shared helpers
                    const audienceMatch = matchesAudience(
                      project,
                      audiences.selectedAudiences
                    );
                    const productGroupMatch = matchesProductGroup(
                      project,
                      productGroups.selectedGroups
                    );
                    const categoryMatch = matchesCategory(
                      project,
                      categories.selectedCats
                    );
                    const productMatch = matchesProduct(
                      project,
                      selectedProducts.selectedProducts
                    );

                    // Product/Category combined logic:
                    // If BOTH products and categories are selected, use OR logic (match either)
                    // If only one is selected, must match that one
                    // If neither selected, pass through
                    const productCategoryMatch =
                      (categories.selectedCats.length === 0 &&
                        selectedProducts.selectedProducts.length === 0) ||
                      (categories.selectedCats.length > 0 &&
                      selectedProducts.selectedProducts.length > 0
                        ? categoryMatch || productMatch
                        : categoryMatch && productMatch);

                    // Apply surface, GenAI, and status filters using shared helpers
                    const surfaceMatch = matchesSurface(
                      project,
                      selectedFeatures.selectedFeatures
                    );
                    const genAiMatch = matchesGenAi(
                      project,
                      genAiFilter.selectedFilters
                    );
                    const statusMatch = matchesStatus(
                      project,
                      status.selectedStatus
                    );

                    const momentTypeMatch =
                      subProjectMatchesMomentTypeSelection(
                        project,
                        effectiveMomentTypeSelection,
                        rsdMomentTypes
                      );

                    // Apply revenue impact filter - check all cards
                    const revenueImpactFilterActive =
                      revenueImpactFilter.selectedFilters.length > 0;
                    const revenueImpactMatch =
                      !revenueImpactFilterActive ||
                      (() => {
                        const hasYesFilter =
                          revenueImpactFilter.selectedFilters.includes(
                            'revenue-impact-yes'
                          );
                        const hasNoFilter =
                          revenueImpactFilter.selectedFilters.includes(
                            'revenue-impact-no'
                          );
                        const revenueImpactValue = hasRevenueImpact(project);

                        // If both Yes and No are selected, show all (no filtering)
                        if (hasYesFilter && hasNoFilter) {
                          return true;
                        }

                        // If only Yes is selected, show only cards with revenueImpact === true
                        if (hasYesFilter && !hasNoFilter) {
                          return revenueImpactValue === true;
                        }

                        // If only No is selected, show cards where revenueImpact is false
                        if (hasNoFilter && !hasYesFilter) {
                          return !revenueImpactValue;
                        }

                        // If neither is selected (shouldn't happen if filter is active), show all
                        return true;
                      })();

                    // Return true only if ALL filters match (AND logic)
                    // Note: eventMatch already handles event highlighting, no need for redundant highlightMatch
                    return (
                      eventMatch &&
                      audienceMatch &&
                      productGroupMatch &&
                      productCategoryMatch &&
                      surfaceMatch &&
                      genAiMatch &&
                      statusMatch &&
                      momentTypeMatch &&
                      revenueImpactMatch
                    );
                  })
                  .map((project, projectIndex) => {
                    // Check if this project should be highlighted
                    const isHighlighted = isProjectHighlighted(project);
                    const dateLabel = getParkingLotDateLabel(project);
                    const tierLabel = getParkingLotTierLabel(project);
                    const titleParts = getRtmPhasedTitleParts({
                      momentType: project.projectType,
                      rawTitle: project.title || '',
                      releasePhase: project.releasePhase,
                      releasePhaseId: (project as any).releasePhaseId,
                      releasePhases: Array.isArray(
                        (project as any).releasePhases
                      )
                        ? ((project as any).releasePhases as any[])
                        : null
                    });
                    const displayTitle = titleParts.displayTitle;
                    const isEolItem = isEolMoment(project.projectType);

                    return (
                      <Flex
                        direction={'row'}
                        key={`project_${itemIndex}_${projectIndex}`}
                        UNSAFE_className={`${styles['parking-lot-item']} ${
                          isHighlighted ? styles['highlighted'] : ''
                        } ${isEolItem ? styles['parking-lot-item-eol'] : ''}`}
                        data-testid={`parking-lot-item-${
                          project?.issueId || projectIndex
                        }`}
                      >
                        <View
                          UNSAFE_className={
                            styles['parking-lot-date-container']
                          }
                          data-testid={`parking-lot-date-${
                            project?.issueId || projectIndex
                          }`}
                        >
                          <Text
                            UNSAFE_className={styles['parking-lot-date-text']}
                          >
                            {dateLabel}
                          </Text>
                        </View>
                        <TooltipTrigger
                          isOpen={(() => {
                            const uniqueTooltipId = getUniqueTooltipId(
                              project,
                              projectIndex
                            );
                            const isOpen = openTooltip === uniqueTooltipId;
                            return isOpen;
                          })()}
                          onOpenChange={isOpen => {
                            if (!isOpen) setOpenTooltip(null);
                          }}
                          placement="top"
                        >
                          <div
                            className={styles['tooltip-wrapper']}
                            onMouseEnter={() =>
                              handleTooltipMouseEnter(project, projectIndex)
                            }
                            onMouseLeave={handleTooltipMouseLeave}
                          >
                            <ActionButton
                              onPress={() =>
                                project && handleCardClick(project)
                              }
                              UNSAFE_className={
                                styles['parking-lot-item-button']
                              }
                              data-testid={`parking-lot-item-button-${
                                project?.issueId || projectIndex
                              }`}
                            >
                              <Text
                                UNSAFE_className={`${
                                  styles['parking-lot-item-text']
                                } ${
                                  isHighlighted
                                    ? styles['highlighted-text']
                                    : ''
                                }`}
                              >
                                <CardTitleDisplay
                                  projectType={project.projectType}
                                  releasePhase={project.releasePhase}
                                  title={project.title || ''}
                                  displayTitle={displayTitle}
                                />
                              </Text>
                            </ActionButton>
                          </div>
                          <Tooltip>
                            {isEolItem
                              ? formatEolPhaseDisplayTitle(
                                  project.releasePhase,
                                  project.title || displayTitle
                                )
                              : displayTitle}
                          </Tooltip>
                        </TooltipTrigger>
                        {/* Right-aligned icons container */}
                        <Flex
                          direction="row"
                          gap="size-75"
                          alignItems="center"
                          UNSAFE_style={{ marginLeft: 'auto' }}
                        >
                          {/* Display revenue impact icon if applicable */}
                          {timelineIconsVisible &&
                            hasRevenueImpact(project) && (
                              <TooltipTrigger
                                isOpen={(() => {
                                  const issueId =
                                    project.issueId || projectIndex;
                                  const releasePhaseId =
                                    project?.releasePhase || 'unknown';
                                  const uniqueRevenueTooltipId = `revenue-impact-${issueId}-${releasePhaseId}`;
                                  return openTooltip === uniqueRevenueTooltipId;
                                })()}
                                onOpenChange={isOpen => {
                                  if (!isOpen) setOpenTooltip(null);
                                }}
                                placement="top"
                              >
                                <div
                                  className={styles['tooltip-wrapper']}
                                  onMouseEnter={() => {
                                    const issueId =
                                      project.issueId || projectIndex;
                                    const releasePhaseId =
                                      project?.releasePhase || 'unknown';
                                    const uniqueRevenueTooltipId = `revenue-impact-${issueId}-${releasePhaseId}`;
                                    handleTooltipMouseEnterWithId(
                                      uniqueRevenueTooltipId
                                    );
                                  }}
                                  onMouseLeave={handleTooltipMouseLeave}
                                >
                                  <ActionButton
                                    isQuiet
                                    UNSAFE_className={
                                      styles['parking-lot-icon-button']
                                    }
                                    aria-label="Revenue Impact"
                                    data-testid={`parking-lot-revenue-impact-${
                                      project?.issueId || projectIndex
                                    }`}
                                  >
                                    <RevenueDriverIcon width={16} height={18} />
                                  </ActionButton>
                                </div>
                                <Tooltip>Impacts Revenue Targets</Tooltip>
                              </TooltipTrigger>
                            )}
                          {/* Display releasePhase icon if available */}
                          {timelineIconsVisible &&
                            project?.releasePhase &&
                            getReleasePhaseIcon(project.releasePhase) && (
                              <TooltipTrigger
                                isOpen={(() => {
                                  const issueId =
                                    project.issueId || projectIndex;
                                  const releasePhaseId =
                                    project?.releasePhase || 'unknown';
                                  const uniquePhaseTooltipId = `release-phase-${issueId}-${releasePhaseId}`;
                                  return openTooltip === uniquePhaseTooltipId;
                                })()}
                                onOpenChange={isOpen => {
                                  if (!isOpen) setOpenTooltip(null);
                                }}
                                placement="top"
                              >
                                <div
                                  className={styles['tooltip-wrapper']}
                                  onMouseEnter={() => {
                                    const issueId =
                                      project.issueId || projectIndex;
                                    const releasePhaseId =
                                      project?.releasePhase || 'unknown';
                                    const uniquePhaseTooltipId = `release-phase-${issueId}-${releasePhaseId}`;
                                    handleTooltipMouseEnterWithId(
                                      uniquePhaseTooltipId
                                    );
                                  }}
                                  onMouseLeave={handleTooltipMouseLeave}
                                >
                                  <ActionButton
                                    isQuiet
                                    UNSAFE_className={
                                      styles['parking-lot-icon-button']
                                    }
                                    aria-label={project.releasePhase}
                                    data-testid={`parking-lot-item-badge-${getReleasePhaseIcon(
                                      project.releasePhase
                                    )!
                                      .replace(/\s+/g, '-')
                                      .toLowerCase()}-${
                                      project?.issueId || projectIndex
                                    }`}
                                  >
                                    {getReleasePhaseIcon(
                                      project.releasePhase
                                    ) === 'beta' ? (
                                      <LegendBetaMiniIcon />
                                    ) : (
                                      <LegendPrivateBetaMiniIcon />
                                    )}
                                  </ActionButton>
                                </div>
                                <Tooltip>{project.releasePhase}</Tooltip>
                              </TooltipTrigger>
                            )}
                          {/* Fallback to labels-based approach for backward compatibility */}
                          {timelineIconsVisible &&
                            !project?.releasePhase &&
                            Array.isArray(project?.labels) &&
                            project.labels.map(
                              (badge, badgeIndex) =>
                                isBetaOrPrivateBetaLabel(badge) && (
                                  <TooltipTrigger
                                    key={badgeIndex}
                                    isOpen={(() => {
                                      const issueId =
                                        project.issueId || projectIndex;
                                      const uniqueLabelTooltipId = `label-${issueId}-${badgeIndex}-${badge}`;
                                      return (
                                        openTooltip === uniqueLabelTooltipId
                                      );
                                    })()}
                                    onOpenChange={isOpen => {
                                      if (!isOpen) setOpenTooltip(null);
                                    }}
                                    placement="top"
                                  >
                                    <div
                                      className={styles['tooltip-wrapper']}
                                      onMouseEnter={() => {
                                        const issueId =
                                          project.issueId || projectIndex;
                                        const uniqueLabelTooltipId = `label-${issueId}-${badgeIndex}-${badge}`;
                                        handleTooltipMouseEnterWithId(
                                          uniqueLabelTooltipId
                                        );
                                      }}
                                      onMouseLeave={handleTooltipMouseLeave}
                                    >
                                      <ActionButton
                                        isQuiet
                                        UNSAFE_className={
                                          styles['parking-lot-icon-button']
                                        }
                                        aria-label={
                                          isBetaLabel(badge)
                                            ? 'Public Beta'
                                            : 'Private Beta'
                                        }
                                        data-testid={`parking-lot-item-badge-${badge
                                          .replace(/\s+/g, '-')
                                          .toLowerCase()}-${
                                          project?.issueId || projectIndex
                                        }`}
                                      >
                                        <Text
                                          UNSAFE_className={
                                            styles['parking-lot-item-badge']
                                          }
                                        >
                                          {isBetaLabel(badge) ? 'B' : 'PB'}
                                        </Text>
                                      </ActionButton>
                                    </div>
                                    <Tooltip>
                                      {isBetaLabel(badge)
                                        ? 'Public Beta'
                                        : 'Private Beta'}
                                    </Tooltip>
                                  </TooltipTrigger>
                                )
                            )}
                          <View
                            UNSAFE_className={styles['parking-lot-tier-badge']}
                            data-testid={`parking-lot-tier-${
                              project?.issueId || projectIndex
                            }`}
                          >
                            <span
                              className={styles['parking-lot-tier-badge-text']}
                            >
                              {tierLabel}
                            </span>
                          </View>
                          {/* Product Icon - shows first product with + indicator for multi-product - rightmost */}
                          {timelineIconsVisible &&
                            project?.products &&
                            project.products.length > 0 && (
                              <TooltipTrigger
                                isOpen={(() => {
                                  const issueId =
                                    project.issueId || projectIndex;
                                  const releasePhaseId =
                                    project?.releasePhase || 'unknown';
                                  const uniqueProductTooltipId = `product-${issueId}-${releasePhaseId}`;
                                  return openTooltip === uniqueProductTooltipId;
                                })()}
                                onOpenChange={isOpen => {
                                  if (!isOpen) setOpenTooltip(null);
                                }}
                                placement="top"
                              >
                                <div
                                  className={styles['tooltip-wrapper']}
                                  onMouseEnter={() => {
                                    const issueId =
                                      project.issueId || projectIndex;
                                    const releasePhaseId =
                                      project?.releasePhase || 'unknown';
                                    const uniqueProductTooltipId = `product-${issueId}-${releasePhaseId}`;
                                    handleTooltipMouseEnterWithId(
                                      uniqueProductTooltipId
                                    );
                                  }}
                                  onMouseLeave={handleTooltipMouseLeave}
                                >
                                  <ActionButton
                                    isQuiet
                                    UNSAFE_className={
                                      styles['parking-lot-icon-button']
                                    }
                                    aria-label="Product"
                                    data-testid={`parking-lot-product-icon-${
                                      project?.issueId || projectIndex
                                    }`}
                                  >
                                    <ProductIconCompact
                                      products={project.products}
                                    />
                                  </ActionButton>
                                </div>
                                <Tooltip>
                                  {Array.isArray(project.products)
                                    ? project.products.length > 1
                                      ? `${project.products[0]} +${
                                          project.products.length - 1
                                        } more`
                                      : project.products[0]
                                    : project.products}
                                </Tooltip>
                              </TooltipTrigger>
                            )}
                        </Flex>
                      </Flex>
                    );
                  })
            )}
        </Flex>
      </div>
    </div>
  );
};
